import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  type CreatePatient as ApiCreatePatient,
  useCreatePatient,
} from "@elation/api-client/src/stitch-web";
import type { Patient } from "@elation/api-client/src/stitch-web/models";

import { navigationV2 } from "@src/shared/navigation/v2";
import { useActor } from "@src/shared/providers/actorProvider";
import { getDisplayErrorMessage } from "@src/shared/utils/errors";
import { captureException } from "@src/shared/utils/observability/sentry";
import { NaiveDate } from "@src/shared/utils/types";

import {
  type CreatePatient,
  type CreatePatientFormValues,
  createPatientFromFormValues,
} from "../../../../domain";

export interface UseCreatePatientFormSubmissionOptions {
  onCreated?: (patient: Patient | null) => Promise<void> | void;
}

export function useCreatePatientFormSubmission({
  onCreated,
}: UseCreatePatientFormSubmissionOptions = {}) {
  const actor = useActor();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createPatient = useCreatePatient();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const isSubmitting = createPatient.isPending;
  const error =
    submissionError ??
    (createPatient.isError
      ? getDisplayErrorMessage(createPatient.error, {
          fallback: "Unable to create patient. Please try again.",
        })
      : null);

  const submitPatient = useCallback(
    async (values: CreatePatient) => {
      setSubmissionError(null);
      if (actor.type !== "physician") {
        const err = new Error("Only physicians can create patients.");
        setSubmissionError(err.message);
        captureException(err, {
          tags: { feature: "patients-v2", action: "create-patient" },
          extra: { actorType: actor.type },
        });
        throw err;
      }

      const data = {
        firstName: values.firstName,
        lastName: values.lastName,
        dob: NaiveDate.toString(values.dob),
        sex: values.sex,
        primaryCarePhysicianId: actor.user.id,
      } satisfies ApiCreatePatient;

      const result = await createPatient.mutateAsync({ data });

      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "/gw/stitch-web/api/patients",
      });

      const patient = result.data.patient ?? null;
      if (onCreated && patient?.id == null) {
        const err = new Error(
          "Patient created, but the response did not include a patient id for follow-up.",
        );
        setSubmissionError(err.message);
        captureException(err, {
          tags: {
            feature: "patients-v2",
            action: "create-patient-missing-id",
          },
        });
        throw err;
      }

      try {
        await onCreated?.(patient);
      } catch (err) {
        const message = getDisplayErrorMessage(err, {
          fallback:
            "Patient created, but follow-up action failed. Please try again.",
        });
        setSubmissionError(message);
        captureException(err, {
          tags: {
            feature: "patients-v2",
            action: "create-patient-on-created",
          },
        });
        throw err;
      }

      if (patient?.id == null) {
        toast.success("Patient created");
        return;
      }

      toast.success("Patient created", {
        action: {
          label: "View patient",
          onClick: () => {
            navigate(navigationV2.patientDetail(String(patient.id)), {
              state: { source: "v2 patient create" },
            });
          },
        },
      });
    },
    [actor, createPatient, navigate, onCreated, queryClient],
  );

  const submitFormValues = useCallback(
    (values: CreatePatientFormValues) =>
      submitPatient(createPatientFromFormValues(values)),
    [submitPatient],
  );

  return {
    error,
    isSubmitting,
    reset: () => {
      setSubmissionError(null);
      createPatient.reset();
    },
    submitFormValues,
  };
}
